/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { IoInformationCircle, IoSparkles } from "react-icons/io5";
import { Link } from "react-router-dom";

import lowerCase from "lodash/lowerCase";
import ReactTable from "react-table";
import { Tooltip as ReactTooltip } from "react-tooltip";

import ClickableTooltip from "../common/ClickableTooltip";

import {
  formatDate,
  getDifferenceBetweenDatesSpecific
} from "../../helpers/date";

import ResponsiveDataComponent from "../common/ResponsiveDataComponent";
import Badge from "../common/Badge";
import Loading from "../common/Loading";

import TableActions from "../common/TableActions";
import { getClassName } from "./SupportTicketHeader";

const getAssignView = (isMobile, isAssigned) => {
  if (!isAssigned) {
    return "Assign";
  }
  return "View";
};

const SupportTicketList = (props) => {
  const {
    serverSide,
    filtered,
    pages,
    isMobile,
    onClickView,
    showResolved,
    defaultPageSize,
    onPageSizeChange,
    minimal,
    redirectOnTicketNumber,
    defaultButtonText,
    onClickReassign,
    auth
  } = props;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (props.data) {
      setData(props.data);
      setLoading(false);
    }
  }, [props.data]);

  const columns = !isMobile
    ? [
        {
          Header: "Priority",
          accessor: "request_priority",
          style: { textAlign: "center" },
          maxWidth: 120,
          minWidth: 105,
          Cell: (row) => {
            const priority = lowerCase(row.value);
            const className = getClassName(priority);
            return (
              <Badge className={`ticket ticket-priority-badge ${className}`}>
                {priority}
              </Badge>
            );
          }
        },
        {
          Header: "Ticket Number",
          accessor: "support_ticket_number",
          maxWidth: 140,
          Cell: (row) =>
            redirectOnTicketNumber ? (
              <Link
                style={{ color: "blue" }}
                to={`/app/support-tickets/${row.original._id}`}
              >
                {row?.value || ""}
              </Link>
            ) : (
              row?.value || ""
            )
        },
        {
          Header: "Status ",
          accessor: "request_status",
          Cell: (row) => (
            <Badge
              color={row?.original.request_status?.color}
              className="ticket-status-badge list-badge"
            >
              {row?.value.label}
            </Badge>
          ),
          maxWidth: 140,
          minWidth: 105
        },
        {
          Header: "Customer",
          accessor: "requestor_company_name",
          Cell: (row) =>
            row.original.customer?.name ? row.original.customer.name : row.value
        },
        { Header: "Request Nature", accessor: "request_nature" },
        {
          Header: "Created Date",
          accessor: "created_on",
          Cell: ({ value }) => (
            <span>
              {formatDate(value, "MMM dd, yyyy")}
              <br />
              {formatDate(value, "hh:mm a")}
            </span>
          )
        },
        {
          Header: "Last Updated",
          accessor: "modified_on",
          Cell: ({ value }) => (
            <span>
              {formatDate(value, "MMM dd, yyyy")}
              <br />
              {formatDate(value, "hh:mm a")}
            </span>
          )
        },

        {
          Header: "Nature",
          accessor: "request_nature_details",
          maxWidth: 70,
          Cell: (cell) => (
            <ClickableTooltip
              custom
              id={`info-${cell.original._id}`}
              isNightMode={auth.isNightMode}
              loading={loading}
              place="bottom"
              tooltipContent={
                <React.Fragment>
                  <b>{cell.original.requestor_full_name}</b>: {cell.value}
                </React.Fragment>
              }
              text={`${
                cell?.original?.requestor_full_name || ""
              }: ${cell?.value || ""}`}
            >
              <IoInformationCircle style={{ color: "#50accb" }} />
            </ClickableTooltip>
          )
        },
        {
          Header: "Assigned To",
          accessor: "assigned_to",
          Cell: ({ original, value }) => {
            if (value) {
              return value.full_name;
            }
            if (original.autoGeneratedEmployeeMatch) {
              return (
                <span>
                  {"Unassigned "}
                  <IoSparkles
                    style={{
                      color: auth.isNightMode ? "#c489dc" : "#4c53d6"
                    }}
                    id="asigned-ai-icon"
                    data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
                  />
                  <ReactTooltip
                    id="asigned-ai-icon-tooltip"
                    place="bottom"
                    anchorId="asigned-ai-icon"
                    className={`custom-tooltip ${
                      auth.isNightMode ? "dark" : "light"
                    }`}
                  >
                    AI Generated Employee Found
                  </ReactTooltip>
                </span>
              );
            }
            return "Unassigned";
          }
        },
        {
          Header: "Location",
          accessor: "location",
          maxWidth: 70
        },
        {
          Header: "Response Due",
          accessor: "response_due",
          Cell: ({ original, value }) => {
            if (value && original.request_status?.label === "In Process") {
              const difference = getDifferenceBetweenDatesSpecific(
                new Date(value),
                new Date()
              );
              const isNegative = difference[0] === "-";
              return (
                <span style={{ color: isNegative ? "red" : "black" }}>
                  {difference}
                </span>
              );
            }
            return value ? formatDate(value, "MM/dd/yyyy") : "";
          }
        }
      ]
    : [
        {
          Header: "Priority",
          accessor: "request_priority",
          style: { textAlign: "center" },
          maxWidth: 80,
          Cell: (row) => {
            const priority = lowerCase(row.value);
            const className = getClassName(priority);
            return <Badge className={`ticket ${className}`}>{priority}</Badge>;
          }
        },
        { Header: "Customer", accessor: "requestor_company_name" }
      ];

  if (onClickReassign) {
    columns.push({
      accessor: "_id",
      filterable: false,
      sortable: false,
      Cell: ({ original }) => (
        <TableActions
          fullViewText="Reassign"
          viewText={getAssignView(
            isMobile,
            original.is_assigned,
            defaultButtonText
          )}
          onClickFullView={() => onClickReassign(original)}
          onClickView={() => onClickView(original)}
        />
      )
    });
  }

  const tableProps = {
    getTdProps: (state, rowInfo, column) => {
      if (column.id === "request_status") {
        return {
          className: "request-status-row"
        };
      }
      if (!column.expander) {
        return {
          onClick: () => {
            if (column.id !== "_id" && column.id !== "request_nature_details") {
              onClickView(rowInfo?.original);
            }
          }
        };
      }
      return {};
    }
  };

  const serverSideProps = {
    manual: true,
    onFetchData: props.fetchData,
    pages,
    filtered,
    loading
  };

  return (
    <ReactTable
      {...tableProps}
      {...(serverSide && serverSideProps)}
      className="-highlight tickets"
      data={
        showResolved
          ? data
          : data.filter((item) => item.request_status.label !== "Resolved")
      }
      columns={columns}
      showPageSizeOptions={!isMobile}
      defaultPageSize={
        minimal && data.length < 5 ? data.length + 1 : defaultPageSize
      }
      {...(minimal && {
        pageSize: minimal && data.length < 5 ? data.length + 1 : defaultPageSize
      })}
      onPageSizeChange={onPageSizeChange}
      LoadingComponent={() => (
        <Loading active={loading || serverSideProps.loading} />
      )}
      showPagination={data.length > 5 || pages > 1}
      SubComponent={
        isMobile
          ? ({ original }) => (
              <ResponsiveDataComponent
                data={[
                  {
                    key: "Support Ticket #",
                    label: original.support_ticket_number
                  },
                  { key: "Company", label: original.requestor_company_name },
                  { key: "Requestor", label: original.requestor_full_name },
                  { key: "Priority", label: original.request_priority }
                ]}
              />
            )
          : false
      }
    />
  );
};

SupportTicketList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  supportTicket: PropTypes.object,
  showResolved: PropTypes.bool,
  filter: PropTypes.object,
  params: PropTypes.object,
  serverSide: PropTypes.bool,
  filtered: PropTypes.object,
  pages: PropTypes.number,
  isMobile: PropTypes.bool,
  onClickView: PropTypes.func,
  fetchData: PropTypes.func,
  defaultPageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  minimal: PropTypes.bool,
  redirectOnTicketNumber: PropTypes.bool,
  defaultButtonText: PropTypes.string,
  onClickReassign: PropTypes.func,
  auth: PropTypes.object
};

SupportTicketList.defaultProps = {
  showResolved: true,
  redirectOnTicketNumber: false,
  defaultButtonText: "",
  onClickReassign: null
};

export default SupportTicketList;
